"use client";

/**
 * ObservabilityProvider — PostHog browser-SDK mount point.
 *
 * Sits between QueryClientProvider and NotificationProvider in the
 * provider tree (see ``plans/PLAN_POSTHOG_01_FOUNDATIONS.md`` §
 * "Provider architecture"). Mounted unconditionally so every consumer
 * can read the context; the ``isEnabled`` flag and ``posthog`` handle
 * are the surface that downstream code branches on.
 *
 * Scope of THIS PR (PR-3)
 * -----------------------
 * - Reads the four ``NEXT_PUBLIC_POSTHOG_*`` env vars.
 * - Computes ``isEnabled`` using the default-on parsing rule from
 *   ``plans/PLAN_POSTHOG_01_FOUNDATIONS.md`` § "Parsing convention":
 *   ``process.env.NEXT_PUBLIC_POSTHOG_ENABLED !== "false"``.
 * - When enabled, calls ``posthog.init(...)`` once on mount with the
 *   exact config bag from § "SDK initialization" → "Browser SDK".
 * - Exposes the posthog-js singleton in context when enabled; ``null``
 *   when disabled. ``ObservabilityBridge`` (below ``AuthProvider``) is
 *   the consumer that drives identify / group / reset / opt-out from
 *   auth state changes.
 */

import { useEffect, useMemo, useRef, type ReactNode } from "react";
import posthog from "posthog-js";
import type { BeforeSendFn } from "@posthog/types";

import {
  ObservabilityContext,
  type ObservabilityContextValue,
} from "@/lib/observability/context";
import { redactEvent } from "@/lib/observability/redactor";

interface ObservabilityProviderProps {
  children: ReactNode;
}

export function ObservabilityProvider({ children }: ObservabilityProviderProps) {
  // Default-on semantics: any value OTHER than the literal string
  // ``"false"`` is treated as enabled. The kill-switch is the explicit
  // ``"false"`` write — set in .env / deployment env when an operator
  // needs to disable the SDK without removing the key from the vault.
  const enabledFlag = process.env.NEXT_PUBLIC_POSTHOG_ENABLED !== "false";
  const hasKey = Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY);
  const isEnabled = enabledFlag && hasKey;
  // Verbose logging — independent of the kill-switch. Default off; set
  // NEXT_PUBLIC_POSTHOG_DEBUG=true in .env.local to observe init + every
  // capture/identify/group/reset call in the browser console.
  const isDebug = process.env.NEXT_PUBLIC_POSTHOG_DEBUG === "true";

  // posthog.init() is idempotent inside the library, but Strict Mode
  // double-invocation of the effect would still trigger a warning. We
  // gate it behind a per-mount ref so init runs at most once.
  const initialized = useRef(false);

  useEffect(() => {
    if (!isEnabled) {
      if (isDebug) {
        console.info(
          "[posthog] init skipped — disabled",
          { enabledFlag, hasKey, reason: !enabledFlag ? "ENABLED=false" : "no key" },
        );
      }
      return;
    }
    if (initialized.current) return;
    if (typeof window === "undefined") return;

    // PostHog ingestion host — hardcoded to the live first-party managed
    // reverse proxy (di.fintum-mi.com → PostHog EU). Routing events
    // through our own domain dodges ad-blockers that would drop
    // ``*.posthog.com``, which matters for the finance-firm browsers our
    // customers use. It is always this one host, so there is no per-env
    // var. (The build-time sourcemap upload still targets the EU app host
    // eu.posthog.com directly — the proxy is for SDK ingestion only.)
    const apiHost = "https://di.fintum-mi.com";

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host: apiHost,
      person_profiles: "identified_only",
      persistence: "memory",
      // Founder decision 2026-06-24: capture every page-view and every
      // click across the whole platform — "which button on which page,
      // by whom, when". This supersedes the earlier curated-only stance;
      // the typed ``retail.*`` / ``marketing.*`` catalog stays as the
      // clean semantic layer ON TOP (named pageviews, filter changes,
      // ask-submit). PII stays guarded: the ``before_send`` redactor
      // strips forbidden keys + URL query/fragments from autocapture
      // payloads, and posthog-js masks input field values by default.
      capture_pageview: "history_change",
      capture_pageleave: true,
      autocapture: true,
      // Web Vitals (LCP / CLS / INP / FCP / TTFB) via the chrome
      // web-vitals library — the platform's only RUM/performance signal
      // (no Grafana). ``network_timing`` rides on session replay, which
      // stays off, so we opt in to ``web_vitals`` only.
      capture_performance: { web_vitals: true },
      // Friction signals: dead clicks (a click that yields no DOM change
      // / navigation) and heatmap data (which also carries the rage-click
      // context). Both part of the no-Grafana observability bar.
      capture_dead_clicks: true,
      capture_heatmaps: true,
      disable_session_recording: true,
      // Exception autocapture — PostHog is the only error stack (no
      // Grafana/Sentry), so this is set EXPLICITLY rather than relying on
      // ``true``'s defaults: both unhandled errors AND unhandled promise
      // rejections are captured. ``capture_console_errors`` stays off —
      // third-party libs ``console.error`` liberally and would bury the
      // real unhandled-error signal. Readable stack traces depend on the
      // ``sourcemaps:upload`` build step (posthog-cli) actually running on
      // deploy. (Resolves Plan_03 open question 03.Q10.)
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      // Redactor — strips URL query strings + fragments, drops
      // forbidden property names, and truncates long strings before
      // dispatch. Runs on every event (auto-captured ``$exception``,
      // pageleaves, ``$identify``, and the catalog events from
      // ``captureEvent``). Resolves Plan_03 open question 03.Q2.
      //
      // The cast bridges ``RedactableEvent`` (loose structural shape
      // used for testability — ``uuid`` / ``timestamp`` optional) to
      // ``BeforeSendFn``'s ``CaptureResult`` input (those fields are
      // required). At runtime PostHog always passes a full
      // ``CaptureResult``; this is the only place the boundary is
      // crossed.
      before_send: redactEvent as unknown as BeforeSendFn,
      // PostHog SDK's built-in verbose logging — turns on extensive
      // console output (init details, every capture, every flush).
      debug: isDebug,
    });
    initialized.current = true;
    if (isDebug) {
      console.info("[posthog] init OK", {
        api_host: apiHost,
        project_id: process.env.NEXT_PUBLIC_POSTHOG_PROJECT_ID,
        persistence: "memory",
        person_profiles: "identified_only",
      });
    }
  }, [isEnabled, isDebug]);

  const value = useMemo<ObservabilityContextValue>(
    () => ({
      // When enabled, hand the imported singleton straight through —
      // posthog-js is module-scoped, so referencing it here is safe
      // even before ``init()`` has resolved (capture calls before init
      // are buffered by the library). When disabled, expose null so
      // downstream branches stay explicit instead of silently no-oping.
      posthog: isEnabled ? posthog : null,
      isEnabled,
    }),
    [isEnabled],
  );

  return (
    <ObservabilityContext.Provider value={value}>
      {children}
    </ObservabilityContext.Provider>
  );
}
